import { useEffect, useReducer, useRef } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { supabase } from '@/lib/supabase';

// State management for auth guard
type State = {
  isChecking: boolean;
  hasDirectSession: boolean;
}

type Action = 
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_DIRECT_SESSION'; payload: boolean };

const initialState: State = {
  isChecking: true,
  hasDirectSession: false,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isChecking: action.payload };
    case 'SET_DIRECT_SESSION':
      return { ...state, hasDirectSession: action.payload };
    default:
      return state;
  }
}

// Define the AuthStore interface
interface AuthStore {
  user?: {
    id: string;
    email?: string;
  } | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthGuardProps {
  children: React.ReactNode;
}

export default function AuthGuard({ children }: AuthGuardProps) {
  const { user, isAuthenticated, isLoading } = useAuthStore() as AuthStore;
  const [state, dispatch] = useReducer(reducer, initialState);
  const location = useLocation();
  const navigate = useNavigate();
  const isOnAuthCallbackPage = location.pathname === '/auth/callback';
  const sessionCheckAttempts = useRef(0);
  const maxSessionCheckAttempts = 3;
  const sessionCheckTimer = useRef<NodeJS.Timeout | null>(null);
  
  // Function to check for a direct Supabase session
  const checkDirectSession = async () => {
    if (user) {
      return true;
    }
    
    // Check for auth completed flag which indicates a successful authentication
    const authCompleted = sessionStorage.getItem('auth_completed');
    if (authCompleted === 'true') {
      console.log('Auth completed flag found, allowing access');
      return true;
    }
    
    try {
      // Check if there's an active supabase session that hasn't been
      // propagated to the authStore yet
      const { data } = await supabase.auth.getSession();
      const hasDirectSession = !!data.session;
      
      if (hasDirectSession) {
        console.log('Direct session found, updating auth store');
        // If we find a direct session but the store hasn't been updated yet,
        // we'll force a refresh to get the user data in sync
        if (!isAuthenticated || !user) {
          // Set the loading state before refreshing the session
          dispatch({ type: 'SET_LOADING', payload: true });
          
          try {
            const { data: refreshData } = await supabase.auth.refreshSession();
            
            // Update the auth store with the refreshed session
            if (refreshData.session && refreshData.user) {
              const authStore = useAuthStore.getState() as any;
              if (authStore.coreStore) {
                authStore.coreStore.setState({
                  user: refreshData.user,
                  isAuthenticated: true,
                  isLoading: false
                });
              }
            }
          } catch (refreshError) {
            console.error('Error refreshing session:', refreshError);
          }
          
          // Reset loading state regardless of the result
          dispatch({ type: 'SET_LOADING', payload: false });
        }
        
        // Return true to allow access
        return true;
      }
      
      // No direct session found, continue with normal checks
      return false;
    } catch (error) {
      console.error('Error checking direct session:', error);
      // If there's an error checking the session, default to normal flow
      return false;
    }
  };

  // Clear any session check timers on unmount
  useEffect(() => {
    return () => {
      if (sessionCheckTimer.current) {
        clearTimeout(sessionCheckTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isLoading) return;

    const handleAuthCheck = async () => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      // First check if there's an authenticated user in the store
      if (isAuthenticated && user) {
        // User is authenticated, nothing to do
        dispatch({ type: 'SET_LOADING', payload: false });
        return;
      }
      
      // If not authenticated in the store, check if there's a direct session
      const hasDirectSession = await checkDirectSession();
      
      if (hasDirectSession) {
        // We found a direct session, access is allowed
        dispatch({ type: 'SET_DIRECT_SESSION', payload: true });
        dispatch({ type: 'SET_LOADING', payload: false });
        return;
      }
      
      // No authentication found, but check if we're in the process of refreshing the page
      // by delaying the redirect slightly to allow Supabase to restore the session
      if (sessionCheckAttempts.current < maxSessionCheckAttempts) {
        sessionCheckAttempts.current += 1;
        console.log(`Authentication check attempt ${sessionCheckAttempts.current}/${maxSessionCheckAttempts}`);
        
        // Don't redirect immediately, wait a bit to see if the session is restored
        sessionCheckTimer.current = setTimeout(async () => {
          // Check once more for a direct session
          const hasDelayedSession = await checkDirectSession();
          
          if (hasDelayedSession) {
            dispatch({ type: 'SET_DIRECT_SESSION', payload: true });
            dispatch({ type: 'SET_LOADING', payload: false });
            return;
          }
          
          // Continue with normal redirect logic after delay if we're not on the auth callback page
          if (!isOnAuthCallbackPage) {
            // Save the current location so we can redirect back after login
            const currentPath = location.pathname + location.search;
            if (!currentPath.includes('/auth/')) {
              sessionStorage.setItem('authRedirectPath', currentPath);
            }
            
            // Check if auth appears to be in progress to prevent redirects during oauth flow
            const authInProgress = sessionStorage.getItem('auth_in_progress');
            if (authInProgress !== 'true') {
              // Redirect to login
              navigate('/auth/login');
            }
          }
          
          dispatch({ type: 'SET_LOADING', payload: false });
        }, 500); // Wait 500ms before deciding to redirect
        
        return;
      }
      
      // No authentication found, we need to redirect to login
      // But first, check if we're already on the auth callback page
      if (isOnAuthCallbackPage) {
        // Don't redirect if we're already on the auth callback page
        dispatch({ type: 'SET_LOADING', payload: false });
        return;
      }
      
      // Also check if auth appears to be in progress (to prevent redirects during oauth flow)
      const authInProgress = sessionStorage.getItem('auth_in_progress');
      if (authInProgress === 'true') {
        // Auth is in progress, delay redirect
        dispatch({ type: 'SET_LOADING', payload: false });
        return;
      }
      
      // Save the current location so we can redirect back after login
      const currentPath = location.pathname + location.search;
      if (!currentPath.includes('/auth/')) {
        sessionStorage.setItem('authRedirectPath', currentPath);
      }
      
      // Redirect to login
      navigate('/auth/login');
      
      dispatch({ type: 'SET_LOADING', payload: false });
    };

    // Run auth check when the auth state changes
    handleAuthCheck();
  }, [isAuthenticated, isLoading, location, navigate, user, isOnAuthCallbackPage]);

  // Show loading state while authentication is being checked
  if (isLoading || state.isChecking) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // If no user is found, check if we have a direct session before redirecting
  if (!user && !state.hasDirectSession) {
    // Check for potential auth in progress before redirecting
    const authInProgress = sessionStorage.getItem('auth_in_progress');
    const authCompleted = sessionStorage.getItem('auth_completed');
    
    // If we're on the auth callback page, don't redirect - let the callback page handle it
    if (isOnAuthCallbackPage) {
      return children;
    }
    
    // If auth appears to be in progress, delay redirect
    if (authInProgress || authCompleted) {
      return (
        <div className="flex flex-col items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mb-4"></div>
          <p className="text-text-secondary dark:text-text-dark-secondary">Verifying authentication...</p>
        </div>
      );
    }
    
    // If this is a protected route and user isn't authenticated, redirect to login
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // User is authenticated or we have a direct session, render the protected content
  return children;
}